import React, { useState, useEffect, useCallback, useMemo } from "react";

// Back End Libraries
import Link from "next/link";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import debounce from "lodash.debounce";

// Front End Libraries
import { Box } from "@mui/material";
import Card from "@mui/material/Card";
import { Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import { Eye } from "lucide-react";

function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (event) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label='first page'>
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label='next page'>
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
				aria-label='last page'>
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

TablePaginationActions.propTypes = {
	count: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	page: PropTypes.number.isRequired,
	rowsPerPage: PropTypes.number.isRequired,
};

export default function FoldersTable({ columns = [], tableData = [], loading = false, onUserUpdated, opened = false }) {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [internalData, setInternalData] = useState([]);
	const [totalItems, setTotalItems] = useState(0);

	const debouncedSearch = useMemo(
		() =>
			debounce(async (searchValue) => {
				try {
					const params = new URLSearchParams({
						page: 1,
						pageSize: rowsPerPage,
						search: searchValue,
						...(opened ? { status: "in verificare" } : {}),
					});

					const response = await fetchWrapper.get(`/api/admin/folders?${params}`);
					setInternalData(response.data);
					setTotalItems(response.total);
					setPage(0);
				} catch (error) {
					console.error("Search error:", error);
				}
			}, 300),
		[rowsPerPage]
	);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	const fetchData = useCallback(async () => {
		try {
			const params = new URLSearchParams({
				page: page + 1,
				pageSize: rowsPerPage,
				search: searchTerm,
				...(opened ? { status: "in verificare" } : {}),
			});

			const response = await fetchWrapper.get(`/api/admin/folders?${params}`);
			setInternalData(response.data);
			setTotalItems(response.total);
		} catch (error) {
			console.error("Fetch error:", error);
		}
	}, [page, rowsPerPage, searchTerm]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const filteredData = React.useMemo(() => {
		if (!searchTerm) return tableData;

		const lowerSearchTerm = searchTerm.toLowerCase();

		return tableData.filter((row) => {
			return Object.keys(row).some((key) => {
				if (typeof row[key] === "string") {
					return row[key].toLowerCase().includes(lowerSearchTerm);
				}
				return false;
			});
		});
	}, [tableData, searchTerm]);

	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

	const tableRows = useMemo(() => {
		return internalData.map((row) => (
			<TableRow key={row.id} hover>
				<TableCell
					style={{
						borderBottom: "1px solid #F7FAFF",
						fontSize: "14px",
					}}>
					<Box
						sx={{
							display: "flex",
							alignItems: "left",
						}}>
						<Typography
							sx={{
								fontWeight: "500",
								fontSize: "14px",
							}}
							as='h5'>
							<Link href={"/admin/folder/" + row.id}>{row.type}</Link>
						</Typography>
					</Box>
				</TableCell>

				<TableCell
					align='left'
					style={{
						fontWeight: 500,
						borderBottom: "1px solid #F7FAFF",
						fontSize: "12px",
					}}>
					<Link href={"/client/" + row.userID}> {row.firstName + " " + row.lastName} </Link>
				</TableCell>

				<TableCell
					align='left'
					style={{
						fontWeight: 500,
						borderBottom: "1px solid #F7FAFF",
						fontSize: "12px",
					}}>
					{(() => {
						const statusConfig = {
							aprobat: { label: "Aprobat", color: "success" },
							"in verificare": { label: "În Verificare", color: "warning" },
							"in procesare": { label: "În Procesare", color: "info" },
							respins: { label: "Respins", color: "error" },
							rezolvat: { label: "Rezolvat", color: "success" },
						};

						const config = statusConfig[row.status] || { label: row.status, color: "default" };

						return <Chip label={config.label} color={config.color} size='small' />;
					})()}
				</TableCell>
			</TableRow>
		));
	}, [internalData]);

	return (
		<Card
			sx={{
				boxShadow: "none",
				borderRadius: "10px",
				p: "25px 25px 10px",
				mb: "15px",
			}}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					paddingBottom: "10px",
					flexWrap: "wrap",
					gap: 2,
				}}>
				<Typography
					as='h3'
					sx={{
						fontSize: 18,
						fontWeight: 500,
					}}>
					<i className='ri-folder-2-line'></i> Dosare{" "}
					{opened ? <strong style={{ color: "green" }}>DESCHISE</strong> : ""}
				</Typography>

				{opened && (
					<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
						<Button
							variant='contained'
							color='primary'
							startIcon={<Eye />}
							sx={{
								borderRadius: "8px",
								textTransform: "none",
								height: "40px",
							}}>
							<Link href='/cereri-dosare' style={{ color: "black", textDecoration: "none" }}>
								Vezi toate dosarele
							</Link>
						</Button>
					</Box>
				)}
			</Box>

			<TableContainer
				component={Paper}
				sx={{
					boxShadow: "none",
					position: "relative",
					minHeight: loading ? "200px" : "auto",
				}}>
				{loading && (
					<Box
						sx={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(13, 16, 21, 0.7)" : "rgba(255, 255, 255, 0.7)",
							zIndex: 1,
						}}>
						<CircularProgress />
					</Box>
				)}

				<Table sx={{ minWidth: 400 }} aria-label='custom pagination table' className='dark-table'>
					<TableHead sx={{ background: "#F7FAFF" }}>
						<TableRow>
							{columns.map((column, index) => (
								<TableCell
									key={index}
									align={"left"}
									style={{
										borderBottom: "1px solid #F7FAFF",
										fontSize: "13.5px",
										fontWeight: 600,
										width: "0px",
									}}>
									{column}
								</TableCell>
							))}
						</TableRow>
					</TableHead>

					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={columns.length} align='center' sx={{ py: 10 }}>
									<CircularProgress />
								</TableCell>
							</TableRow>
						) : (
							<>
								{tableRows}
								{internalData.length === 0 && (
									<TableRow>
										<TableCell colSpan={columns.length} align='center' sx={{ py: 3 }}>
											<Typography>Nicio cerere de dosar deschisa.</Typography>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>

					<TableFooter>
						<TableRow>
							<TablePagination
								rowsPerPageOptions={[5, 10, 25]}
								colSpan={columns.length}
								count={totalItems}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								ActionsComponent={TablePaginationActions}
							/>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</Card>
	);
}

FoldersTable.propTypes = {
	tableData: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string.isRequired,
			userImg: PropTypes.string,
		})
	),
	loading: PropTypes.bool,
};

FoldersTable.defaultProps = {
	tableData: [],
	loading: false,
};
